import type {
	ApiState,
	ExtendedFetchOptions
} from '@/types/api-client'
import type {
	ActivationDiscountsPayload,
	ActivationDiscountsResponse,
	SetHasDiscountPayload,
	SetHasDiscountResponse
} from '@/types/api-client/account-discounts'
import type { FetchOptions } from 'ofetch'

/**
 * Account Discount API helper
 * Manages discount flags and details for accounts in pre-installation status
 */
export function createAccountDiscountApi(
	request: <T>(endpoint: string, options?: ExtendedFetchOptions) => Promise<T | null>,
	_createApiState: <T>(endpoint: string, options?: FetchOptions) => ApiState<T>
) {
	return {
		/**
		 * Set hasDiscount flag for an account (for statuses: Lead, Prospect, Opportunity, Pending Install)
		 * @param accountId - The account ID
		 * @param payload - Request body containing hasDiscount boolean
		 * @param options - Optional fetch configuration
		 * @returns Promise<SetHasDiscountResponse | null>
		 * @example
		 * ```typescript
		 * await api.accountDiscount.setHasDiscount(123, { hasDiscount: true })
		 * ```
		 */
		setHasDiscount: (
			accountId: number,
			payload: SetHasDiscountPayload,
			options: ExtendedFetchOptions = {}
		) => {
			return request<SetHasDiscountResponse>(
				`/portal/accounts/${accountId}/hasDiscount`,
				{
					method: 'PUT',
					body: payload,
					...options,
					errorsAllowed: options.errorsAllowed || {
						titles: { '': 'errors.account_discounts.update_discount_title' },
						messages: {
							'Missing hasDiscount': 'errors.account_discounts.missing_has_discount',
							'hasDiscount must be True or False': 'errors.account_discounts.has_discount_boolean',
							'Account not found': 'errors.account_discounts.account_not_found',
							'account not found': 'errors.account_discounts.account_not_found',
							'Account {accountId} was not found': 'errors.account_discounts.account_id_not_found',
							'An unexpected error has occurred.': 'errors.account_discounts.unexpected_error',
							'You cannot modify any account beyond Pending Install': 'errors.account_discounts.cannot_modify_beyond_pending_install',
							'': 'errors.account_discounts.update_discount_message'
						}
					}
				}
			)
		},

		/**
		 * Set activation discount details for an account
		 * @param accountId - The account ID
		 * @param payload - Discount details including amounts, labels, duration
		 * @param options - Optional fetch configuration
		 * @returns Promise<ActivationDiscountsResponse | null>
		 * @example
		 * ```typescript
		 * await api.accountDiscount.setActivationDiscounts(123, {
		 *   noDiscounts: 0,
		 *   installationDiscount: 100,
		 *   installationDiscountLabel: 'Holiday Special',
		 *   serviceDiscount: 25,
		 *   serviceDiscountLabel: 'First Year Promo',
		 *   serviceDiscountMonths: 12,
		 *   permanentServiceDiscount: false
		 * })
		 * ```
		 */
		setActivationDiscounts: (
			accountId: number,
			payload: ActivationDiscountsPayload,
			options: ExtendedFetchOptions = {}
		) => {
			return request<ActivationDiscountsResponse>(
				`/portal/accounts/${accountId}/activation/discounts`,
				{
					method: 'POST',
					body: payload,
					...options,
					errorsAllowed: options.errorsAllowed || {
						titles: { '': 'errors.account_discounts.activation_discount_title' },
						messages: {
							'Missing required account-id': 'errors.account_discounts.missing_account_id',
							'Account must have a status of opportunity or pending install': 'errors.account_discounts.invalid_status_for_discounts',
							'noDiscounts can only be 0 or 1': 'errors.account_discounts.invalid_no_discounts_value',
							'Come on now, if you are going to allow discounts add them': 'errors.account_discounts.no_discount_fields_entered',
							'If you want a permanent discount for service then you need to include it': 'errors.account_discounts.permanent_discount_required',
							'A permanent service discount requires a good label': 'errors.account_discounts.permanent_discount_label_required',
							'': 'errors.account_discounts.activation_discount_message'
						}
					}
				}
			)
		}
	}
}
